import { useEffect, useRef, useState } from 'react';

import { colour } from './theme';

/**
 * The formula editor: MathLive's `<math-field>`, wrapped so the rest of the app never sees it.
 *
 * ## Why a wrapper and not the element directly
 *
 * MathLive is a custom element with global, one-shot configuration and a five-megabyte package.
 * Three things have to be true wherever it is used, and none of them is the caller's business:
 *
 * 1. **It is loaded on demand.** A static `import` would put the whole engine in the main
 *    bundle, so every user pays for it on first paint whether or not they ever type a formula.
 *    The dynamic import puts it in a chunk that arrives when the panel is opened.
 * 2. **It is configured once.** `fontsDirectory` is a *static* property shared by every field
 *    on the page. Two components setting it is a race whose winner depends on mount order.
 * 3. **It falls back.** If the engine cannot be loaded at all, a plain text field takes over
 *    rather than leaving the user with nothing — the formula is LaTeX either way, so the two
 *    edit the same string and produce the same document.
 *
 * ## Fonts are served locally, not from a CDN
 *
 * MathLive's documented setup points `fontsDirectory` at jsdelivr. That is wrong here: this
 * application runs from a static build with no server of its own, and a classroom's wifi is
 * not something to make a formula editor depend on. Missing fonts do not fail loudly either —
 * the field keeps working and renders every glyph as a box. `scripts/copy-mathlive-fonts.mjs`
 * copies them out of the package at build time, so the whole thing works offline.
 */

/** Where `copy-mathlive-fonts.mjs` puts them. Relative, so a sub-path deployment still works. */
const FONTS_DIRECTORY = './mathlive-fonts';

/**
 * Loads and configures MathLive, once per page.
 *
 * The promise is cached rather than the module, so concurrent callers during the first load
 * share one request instead of racing to configure the same static property.
 */
let loading: Promise<boolean> | null = null;

function loadMathLive(): Promise<boolean> {
  loading ??= import('mathlive')
    .then(({ MathfieldElement }) => {
      MathfieldElement.fontsDirectory = FONTS_DIRECTORY;
      // No sound: this is used at the front of a classroom.
      MathfieldElement.soundsDirectory = null;
      return true;
    })
    .catch(() => false);

  return loading;
}

export interface MathFieldProps {
  /** LaTeX. The same string the document stores, so nothing is converted on the way in or out. */
  readonly value: string;
  readonly onChange: (latex: string) => void;
  /** Enter, which the panel uses to commit. */
  readonly onSubmit?: () => void;
  readonly invalid?: boolean;
  /**
   * Filled in with a way to insert into the field, once there is a field to insert into.
   *
   * Needed because inserting is **not** the same as setting the value, and the difference is
   * the whole usefulness of a template. Assigning `value` replaces the formula and leaves the
   * caret at the end; `insert` puts the fragment where the caret is and — the part that
   * matters — moves the selection into the first `\placeholder{}` of what it inserted. That is
   * what turns pressing "phân số" into a fraction with the numerator ready to type, instead of
   * a fraction the writer has to click into.
   */
  readonly handle?: { current: MathFieldHandle | null };
}

export interface MathFieldHandle {
  /** Inserts at the caret and selects the first placeholder inside what was inserted. */
  insert: (latex: string) => void;
  focus: () => void;
}

export function MathField({ value, onChange, onSubmit, invalid = false, handle }: MathFieldProps) {
  const host = useRef<HTMLDivElement | null>(null);
  const field = useRef<HTMLElement & { value: string }>(null);
  const [ready, setReady] = useState<boolean | null>(null);

  /*
   * The callbacks are mirrored into a ref so the effect below can run **once**.
   *
   * Re-creating the element whenever a parent re-renders would throw away the caret, the
   * selection and any in-progress input — which is every keystroke, since typing changes the
   * value and re-renders the panel.
   */
  const handlers = useRef({ onChange, onSubmit });
  useEffect(() => {
    handlers.current = { onChange, onSubmit };
  });

  useEffect(() => {
    let cancelled = false;

    void loadMathLive().then((loaded) => {
      if (cancelled) return;
      setReady(loaded);
      if (!loaded || host.current === null) return;

      const element = document.createElement('math-field') as HTMLElement & { value: string };
      element.value = value;
      element.style.width = '100%';
      element.style.fontSize = '17px';
      // The virtual keyboard is for tablets; on a desktop it covers the drawing.
      element.setAttribute('math-virtual-keyboard-policy', 'manual');

      element.addEventListener('input', () => {
        handlers.current.onChange(element.value);
      });
      element.addEventListener('keydown', (event) => {
        // Kept away from the canvas shortcuts, or typing `v` in a formula switches tools.
        event.stopPropagation();
        if (event.key === 'Enter') handlers.current.onSubmit?.();
      });

      if (handle !== undefined) {
        const mathfield = element as HTMLElement & {
          value: string;
          insert?: (latex: string, options?: Record<string, unknown>) => boolean;
          focus: () => void;
        };
        handle.current = {
          insert: (latex) => {
            mathfield.insert?.(latex, { selectionMode: 'placeholder', focus: true });
            handlers.current.onChange(mathfield.value);
          },
          focus: () => {
            mathfield.focus();
          },
        };
      }

      host.current.replaceChildren(element);
      field.current = element;
      element.focus();
    });

    return () => {
      cancelled = true;
    };
    // Deliberately once: see the note on `handlers`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Pushed in only when it actually differs.
   *
   * Assigning on every render would reset the caret to the end mid-word. The comparison is
   * what makes the field controlled from outside — the examples buttons replace the formula —
   * without it fighting the user for the cursor.
   */
  useEffect(() => {
    const element = field.current;
    if (element !== null && element.value !== value) element.value = value;
  }, [value]);

  if (ready === false) {
    return (
      <input
        value={value}
        spellCheck={false}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter') onSubmit?.();
        }}
        aria-label="Công thức"
        style={{
          padding: '5px 7px',
          borderRadius: 4,
          border: `1px solid ${invalid ? colour.danger : colour.line}`,
          font: '13px/1.3 var(--es-mono)',
        }}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: 34,
        padding: '2px 4px',
        borderRadius: 4,
        border: `1px solid ${invalid ? colour.danger : colour.line}`,
        background: colour.solid,
      }}
    >
      {ready === null && <span style={{ color: colour.faint, fontSize: 11 }}>đang tải…</span>}
      {/*
        The custom element goes in a node of its own, which React renders empty and never
        touches again.
        Mounting it into a node that also holds React's children was the first version and it
        crashed: `replaceChildren` removes the placeholder behind React's back, and React then
        tries to remove a node that is no longer its child. An escape hatch out of the virtual
        DOM has to be a *whole* node, not a corner of one.
      */}
      <div ref={host} />
    </div>
  );
}
